import { useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { trackEvent } from '@/reporting/analytics';
import { investmentSizeOptions, parseWaitlist } from './schema';
import { useTurnstile } from './useTurnstile';

const WAITLIST_API_URL =
  import.meta.env.VITE_WAITLIST_API_URL ??
  'https://app.novaquantis.ai/api/waitlist';
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

/**
 * The app-bff's `nqCorrelationId` middleware rejects requests without an
 * `x-nq-correlation-id` header (400 "Missing x-nq-correlation-id header").
 * The main app injects it via its axios interceptor; the marketing site has no
 * such client, so we generate one per request here.
 */
function correlationId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `nq-www-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

type Status = 'idle' | 'submitting' | 'success' | 'error';

interface WaitlistModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const initialForm = { name: '', email: '', investmentSize: '' };

export function WaitlistModal({ open, onOpenChange }: WaitlistModalProps) {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>('idle');
  const turnstileRef = useRef<HTMLDivElement>(null);
  const turnstileToken = useTurnstile(TURNSTILE_SITE_KEY, turnstileRef, open);

  const updateField =
    (field: keyof typeof initialForm) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const value = event.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
    };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const parsed = parseWaitlist(form);
    if (!parsed.ok) {
      setErrors(parsed.errors);
      trackEvent('waitlist_submit', { status: 'invalid' });
      return;
    }

    setErrors({});
    setStatus('submitting');

    const payload = TURNSTILE_SITE_KEY
      ? { ...parsed.data, turnstileToken }
      : parsed.data;

    try {
      const response = await fetch(WAITLIST_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-nq-correlation-id': correlationId(),
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(`Waitlist request failed: ${response.status}`);
      }
      setStatus('success');
      trackEvent('waitlist_submit', {
        status: 'success',
        investmentSize: parsed.data.investmentSize,
      });
    } catch (error) {
      console.error('Waitlist submission failed:', error);
      setStatus('error');
      trackEvent('waitlist_submit', { status: 'error' });
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setForm(initialForm);
      setErrors({});
      setStatus('idle');
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Join the waitlist</DialogTitle>
          <DialogDescription>
            Be first to know when NovaQuantis opens to new investors.
          </DialogDescription>
        </DialogHeader>

        {status === 'success' ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium text-foreground">
              Thanks! We&rsquo;ve received your details.
            </p>
            <p className="text-sm text-muted-foreground">
              We&rsquo;ll be in touch when we&rsquo;re ready.
            </p>
          </div>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="waitlist-name">Name</Label>
              <Input
                id="waitlist-name"
                name="name"
                autoComplete="name"
                placeholder="Your name"
                value={form.name}
                onChange={updateField('name')}
                aria-invalid={Boolean(errors.name)}
                aria-describedby={errors.name ? 'waitlist-name-error' : undefined}
              />
              {errors.name && (
                <span id="waitlist-name-error" className="text-sm text-destructive">
                  {errors.name}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="waitlist-email">Email</Label>
              <Input
                id="waitlist-email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={updateField('email')}
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? 'waitlist-email-error' : undefined}
              />
              {errors.email && (
                <span id="waitlist-email-error" className="text-sm text-destructive">
                  {errors.email}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="waitlist-size">Investment size</Label>
              <select
                id="waitlist-size"
                name="investmentSize"
                value={form.investmentSize}
                onChange={updateField('investmentSize')}
                aria-invalid={Boolean(errors.investmentSize)}
                aria-describedby={
                  errors.investmentSize ? 'waitlist-size-error' : undefined
                }
                className="flex h-10 w-full appearance-none rounded-md border border-solid border-input bg-muted px-3 py-2 text-sm text-foreground transition-colors focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
              >
                <option value="">Select range</option>
                {investmentSizeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.investmentSize && (
                <span
                  id="waitlist-size-error"
                  className="text-sm text-destructive"
                >
                  {errors.investmentSize}
                </span>
              )}
            </div>

            {TURNSTILE_SITE_KEY && <div ref={turnstileRef} />}

            {status === 'error' && (
              <span role="alert" className="text-sm text-destructive">
                Something went wrong. Please try again.
              </span>
            )}

            <Button
              type="submit"
              variant="green"
              disabled={
                status === 'submitting' ||
                (Boolean(TURNSTILE_SITE_KEY) && !turnstileToken)
              }
            >
              {status === 'submitting' ? (
                <>
                  <Loader2 className="animate-spin" />
                  Submitting…
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
